import axios from "axios";

console.log('🔧 API Configuration Loading...');

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4001",
  withCredentials: false,
});

console.log('🌐 API Base URL:', api.defaults.baseURL);
console.log('🍪 With Credentials:', api.defaults.withCredentials);

// ---- Token storage helpers
type StoredAuth = {
  accessToken: string | null;
  refreshToken: string | null;
};

let tokens: StoredAuth = {
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
};

console.log('🔑 Initial tokens:', {
  hasAccessToken: !!tokens.accessToken,
  hasRefreshToken: !!tokens.refreshToken
});

export const setTokens = (at: string | null, rt: string | null) => {
  console.log('💾 Setting tokens:', { hasAccessToken: !!at, hasRefreshToken: !!rt });
  tokens.accessToken = at;
  tokens.refreshToken = rt;
  if (at) localStorage.setItem("accessToken", at); else localStorage.removeItem("accessToken");
  if (rt) localStorage.setItem("refreshToken", rt); else localStorage.removeItem("refreshToken");
};

// Request interceptor with debug logging
api.interceptors.request.use((config) => {
  console.log('📤 Making API request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
    hasAuth: !!tokens.accessToken,
    data: config.data
  });
  
  if (tokens.accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

// ---- Refresh flow on 401
let refreshing = false;
let queue: Array<() => void> = [];

api.interceptors.response.use(
  (res) => {
    console.log('📥 API Response received:', {
      status: res.status,
      statusText: res.statusText,
      url: res.config.url
    });
    return res;
  },
  async (err) => {
    console.error('💥 API Response error:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      url: err.config?.url,
      message: err.message
    });

    // Handle maintenance mode (503)
    if (err.response?.status === 503) {
      const errorData = err.response?.data?.error;
      if (errorData?.code === 'MAINTENANCE_MODE') {
        console.log('🚧 System in maintenance mode');
        // Redirect to maintenance page or show modal
        window.location.href = '/maintenance';
        return Promise.reject(err);
      }
    }

    const original = err.config;
    if (err.response?.status === 401 && !original._retry && tokens.refreshToken) {
      console.log('🔄 Attempting token refresh...');
      original._retry = true;
      try {
        if (refreshing) {
          console.log('⏳ Already refreshing, waiting...');
          await new Promise<void>((resolve) => queue.push(resolve));
        } else {
          refreshing = true;
          console.log('🔃 Starting token refresh...');
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL || "http://localhost:4001"}/api/auth/refresh`,
            { refreshToken: tokens.refreshToken }
          );
          
          // backend returns { ok:true, data:{ accessToken, refreshToken } } or { ok:true, accessToken, refreshToken }
          const at = data?.accessToken ?? data?.data?.accessToken;
          const rt = data?.refreshToken ?? data?.data?.refreshToken;
          setTokens(at, rt);
          refreshing = false;
          queue.forEach((fn) => fn());
          queue = [];
          console.log('✅ Token refresh successful');
        }
        // retry original with new AT
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return api(original);
      } catch (e) {
        console.error('❌ Token refresh failed:', e);
        refreshing = false;
        queue = [];
        setTokens(null, null);
        // let app handle unauthorized (e.g., redirect to login)
      }
    }
    return Promise.reject(err);
  }
);

export default api;